import NsTable from '@britishgasenergy/react-nucleus/dist/nuc-components/NsTable';
import NsContent from 'components/common/ns-content/NsContent';
import { shallowEqual, useSelector } from 'react-redux';
import {
  COLOR_GREYSCALE_WHITE,
  COLOR_GREYSCALE_MEDIUM,
  THEME_BORDER_WIDTH_SMALL
} from '@connectedhomes/nucleus/build/tokens/es6/tokens';

import { selectIsLoading } from 'state/quote/quote-slice';
import { selectIsLoadingPost as selectIsLoadingAbandonedCart } from 'state/abandoned-cart/abandoned-cart-slice';
import { FUEL_TYPES } from 'config/app-config';
import {
  formatTariffDate,
  getTariffEndDate
} from 'components/direct-sales/quote/utils';
import { SelectTariffCta } from 'components/direct-sales/quote/components/SelectTariffCta';
import { RowTitle } from 'components/direct-sales/quote/components/TariffTable/RowTitle';
import { UnitValue } from 'components/direct-sales/quote/components/TariffTable/UnitValue';
import { FuelDetail } from 'components/direct-sales/quote/components/TariffTable/FuelDetail';
import { HeaderTh } from 'components/direct-sales/quote/components/TariffTable/HeaderTh';
import { ExitFee } from 'components/direct-sales/quote/components/TariffTable/ExitFee';
import { TariffTableSkeleton } from 'components/direct-sales/quote/components/TariffTable/TariffTableSkeleton';

import type { TProduct } from 'api/models/TProduct';

export const testId = 'tariffTableTestId';
export const headingTestId = 'headingTestId';
export const unitRateRowTestId = 'unitRateRowTestId';
export const standingChargeRowTestId = 'standingChargeRowTestId';
export const exitFeeRowTestId = 'exitFeeRowTestId';
export const tariffEndsOnRowTestId = 'tariffEndsOnRowTestId';
export const ctaRowTestId = 'ctaRowTestId';

type Props = {
  products?: TProduct[];
};

export const TariffTable = ({ products = [] }: Props) => {
  const isLoadingTariff = useSelector(selectIsLoading, shallowEqual);
  const isLoadingAbandonedCart = useSelector(selectIsLoadingAbandonedCart, shallowEqual);
  const hasProducts = products?.length > 0;
  const tdStyle = {
    border: `${THEME_BORDER_WIDTH_SMALL} solid ${COLOR_GREYSCALE_MEDIUM}`,
    backgroundColor: COLOR_GREYSCALE_WHITE
  };

  if (isLoadingTariff || isLoadingAbandonedCart) {
    return <TariffTableSkeleton />;
  }

  if (!hasProducts) {
    return null;
  }

  return (
    <NsTable>
      <table data-testid={testId}>
        <thead>
          <tr data-testid={headingTestId}>
            {products?.map((product) => (
              <HeaderTh
                key={product.code}
                product={product}
                testId={`${headingTestId}-${product.code}`}
                style={tdStyle}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          <RowTitle colSpan={products.length}>
            Unit rate (cost per kWh)
          </RowTitle>
          <tr data-testid={unitRateRowTestId}>
            {products?.map(({ code, gasTil, electricityTil }) => (
              <td
                key={code}
                data-testid={`${unitRateRowTestId}-${code}`}
                style={tdStyle}
              >
                <NsContent spacing="cosy">
                  <FuelDetail fuel={FUEL_TYPES.GAS} til={gasTil}>
                    <UnitValue>{gasTil?.unitRate}p</UnitValue>
                  </FuelDetail>

                  {electricityTil?.unitRate ? (
                    <FuelDetail fuel={FUEL_TYPES.ELECTRIC} til={electricityTil}>
                      <UnitValue>{electricityTil?.unitRate}p</UnitValue>
                    </FuelDetail>
                  ) : (
                    <FuelDetail fuel={FUEL_TYPES.ELECTRIC} til={electricityTil}>
                      <UnitValue unit={`(${electricityTil?.unitRateDayName})`}>
                        {electricityTil?.unitRateDay}p
                      </UnitValue>

                      <UnitValue
                        unit={`(${electricityTil?.unitRateNightName})`}
                      >
                        {electricityTil?.unitRateNight}p
                      </UnitValue>
                    </FuelDetail>
                  )}
                </NsContent>
              </td>
            ))}
          </tr>

          <RowTitle colSpan={products.length}>
            Standing charge (cost per day)
          </RowTitle>
          <tr data-testid={standingChargeRowTestId}>
            {products?.map(({ code, gasTil, electricityTil }) => (
              <td
                key={code}
                data-testid={`${standingChargeRowTestId}-${code}`}
                style={tdStyle}
              >
                <NsContent spacing="cosy">
                  <FuelDetail fuel={FUEL_TYPES.GAS} til={gasTil}>
                    <UnitValue>{gasTil?.standingCharge}p</UnitValue>
                  </FuelDetail>

                  <FuelDetail fuel={FUEL_TYPES.ELECTRIC} til={electricityTil}>
                    <UnitValue>{electricityTil?.standingCharge}p</UnitValue>
                  </FuelDetail>
                </NsContent>
              </td>
            ))}
          </tr>

          <RowTitle colSpan={products.length}>Exit Fee</RowTitle>
          <tr data-testid={exitFeeRowTestId}>
            {products?.map((product) => (
              <td
                key={product.code}
                data-testid={`${exitFeeRowTestId}-${product.code}`}
                style={tdStyle}
              >
                <NsContent spacing="cosy">
                  <NsContent spacing="compact">
                    <ExitFee product={product} />
                  </NsContent>
                </NsContent>
              </td>
            ))}
          </tr>

          <RowTitle colSpan={products.length}>Tariff end date</RowTitle>
          <tr data-testid={tariffEndsOnRowTestId}>
            {products?.map((product) => (
              <td
                key={product.code}
                data-testid={`${tariffEndsOnRowTestId}-${product.code}`}
                style={tdStyle}
              >
                <NsContent spacing="cosy">
                  <NsContent spacing="compact">
                    {getTariffEndDate(product)
                      ? formatTariffDate(getTariffEndDate(product))
                      : 'Not Applicable'}
                  </NsContent>
                </NsContent>
              </td>
            ))}
          </tr>

          <tr data-testid={ctaRowTestId}>
            {products?.map((product) => (
              <td
                key={product.code}
                data-testid={`${ctaRowTestId}-${product.code}`}
                style={tdStyle}
              >
                <SelectTariffCta product={product}>
                  Pick this tariff
                </SelectTariffCta>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </NsTable>
  );
};
